import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  SimpleChanges,
} from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LangService } from 'src/app/services/lang.service';

@Component({
  selector: 'app-text-to-speech-btn',
  templateUrl: './text-to-speech-btn.component.html',
  styleUrls: ['./text-to-speech-btn.component.scss'],
})
export class TextToSpeechBtnComponent implements OnInit, OnChanges, OnDestroy {
  @Input() textContent: string = '';
  @Input() autoStart: boolean = false;

  public isReading: boolean = false;
  public isPaused: boolean = false;
  private speechSynthesis: SpeechSynthesis;
  private utterance: SpeechSynthesisUtterance | null = null;
  private maxTextLength: number = 32767;
  private navigationSubscription: any;
  private beforeUnloadHandler: () => void;
  private currentLang: string = 'nl';

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private langService: LangService
  ) {
    this.speechSynthesis = window.speechSynthesis;
    this.beforeUnloadHandler = () => this.stopTextToSpeech();
  }

  ngOnInit(): void {
    this.langService.language.subscribe((lang) => {
      this.currentLang = lang || 'nl';
    });

    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.stopTextToSpeech();
      });

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['textContent'] && !changes['textContent'].firstChange) {
      this.stopTextToSpeech();
    }
  }

  toggleTextToSpeech(): void {
    if (this.isReading) {
      if (this.isPaused) {
        this.resumeSpeech();
      } else {
        this.pauseSpeech();
      }
    } else {
      this.startTextToSpeech();
    }
  }

  startTextToSpeech(): void {
    this.stopTextToSpeech();

    let textToRead = this.textContent;

    if (!textToRead) {
      return;
    }

    if (!this.speechSynthesis) {
      console.error('Speech synthesis not supported in this browser');
      return;
    }

    const voices = this.speechSynthesis.getVoices();
    if (voices.length === 0) {
      this.speechSynthesis.onvoiceschanged = () => {
        this.startTextToSpeech();
      };
      return;
    }

    if (textToRead.length > this.maxTextLength) {
      textToRead = textToRead.substring(0, this.maxTextLength);
    }

    this.utterance = new SpeechSynthesisUtterance(textToRead);
    this.utterance.lang = this.currentLang;
    this.utterance.rate = 1.0;
    this.utterance.pitch = 1.0;
    this.utterance.volume = 1.0;

    const matchingVoices = voices.filter(voice => voice.lang.startsWith(this.currentLang));
    if (matchingVoices.length > 0) {
      this.utterance.voice = matchingVoices[0];
    }

    this.utterance.onstart = () => {
      this.isReading = true;
      this.isPaused = false;
      this.cdr.detectChanges();
    };

    this.utterance.onend = () => {
      this.isReading = false;
      this.isPaused = false;
      this.utterance = null;
      this.cdr.detectChanges();
    };

    this.utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event.error);
      this.isReading = false;
      this.isPaused = false;
      this.utterance = null;
      this.cdr.detectChanges();
    };

    setTimeout(() => {
      try {
        this.speechSynthesis.speak(this.utterance!);
      } catch (error) {
        console.error('Exception when calling speak():', error);
      }
    }, 100);
  }

  pauseSpeech(): void {
    if (this.speechSynthesis.speaking && !this.speechSynthesis.paused) {
      this.speechSynthesis.pause();
      this.isPaused = true;
      this.cdr.detectChanges();
    }
  }

  resumeSpeech(): void {
    if (this.speechSynthesis.paused) {
      this.speechSynthesis.resume();
      this.isPaused = false;
      this.cdr.detectChanges();
    }
  }

  stopTextToSpeech(): void {
    if (this.speechSynthesis.speaking || this.speechSynthesis.pending) {
      this.speechSynthesis.cancel();
    }
    this.isReading = false;
    this.isPaused = false;
    this.utterance = null;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.stopTextToSpeech();
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }
}
